import * as React from 'react';
import { useState } from 'react';
import { Alert, Button, Flex, FlexItem, FormGroup, Modal, ModalVariant, Radio, Stack, StackItem, Switch, TextInput } from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { useQueryClient } from '@tanstack/react-query';

import { ClusterRulesEditor, DraftRule, withUid } from './ClusterRulesEditor';
import { GroupPicker } from './GroupPicker';
import { describeChanges } from './historyDiff';
import { Group } from './parseGroups';
import { Fetcher, FlagInfo, HistoryEvent } from './types';

/** The editable panel under one row of the App Configs table. */
export const FlagEditor: React.FC<{
  flag: FlagInfo;
  clusters: string[];
  suggestions: Group[];
  mine: Group[];
  api: Fetcher;
  notice?: string;
  onChanged: (notice: string) => void;
}> = ({ flag, clusters, suggestions, mine, api, notice, onChanged }) => {
  const initial = flag.saved;
  const [enabled, setEnabled] = useState<boolean>(initial ? initial.enabled : flag.effective.enabled);
  const [audienceMode, setAudienceMode] = useState<'all' | 'groups'>(initial?.audienceMode ?? 'all');
  const [groups, setGroups] = useState<Group[]>(initial?.audienceGroups ?? []);
  const [stringValue, setStringValue] = useState<string>(initial?.stringValue ?? flag.effective.stringValue ?? '');
  const [rules, setRules] = useState<DraftRule[]>(
    (Array.isArray(initial?.jsonValue) ? (initial!.jsonValue as DraftRule[]) : flag.envDefault.clusterRules ?? []).map(withUid)
  );
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'success' | 'danger'; text: string } | null>(notice ? { kind: 'success', text: notice } : null);
  const [history, setHistory] = useState<HistoryEvent[] | null>(null);
  const [showRaw, setShowRaw] = useState(false);
  const queryClient = useQueryClient();

  const run = async (action: () => Promise<void>, ok: string) => {
    setBusy(true);
    setMessage(null);
    try {
      await action();
      queryClient.invalidateQueries({ queryKey: ['appConfig'] });
      // The page reloads the saved state and rebuilds this panel, so the notice is passed up.
      onChanged(ok);
    } catch (e) {
      setMessage({ kind: 'danger', text: e instanceof Error ? e.message : 'Request failed' });
    } finally {
      setBusy(false);
    }
  };

  const save = () =>
    run(async () => {
      await api(`/api/app-config/flags/${flag.key}`, {
        method: 'PUT',
        body: JSON.stringify({
          enabled,
          audienceMode: flag.supportsAudience ? audienceMode : 'all',
          audienceGroups: flag.supportsAudience && audienceMode === 'groups' ? groups : [],
          stringValue: flag.stringParamLabel ? stringValue : null,
          jsonValue: flag.hasClusterRules
            ? rules.map(r => ({ cluster: r.cluster, enabled: r.enabled, groups: r.groups, groupPattern: r.groupPattern }))
            : null,
        }),
      });
    }, 'Saved. Changes apply within about 30 seconds.');

  const reset = () =>
    run(async () => {
      await api(`/api/app-config/flags/${flag.key}`, { method: 'DELETE' });
    }, flag.valueOnly ? 'Cleared. The environment value is used again.' : 'Reset to the environment default.');

  const showHistory = async () => {
    try {
      setHistory((await api<{ events: HistoryEvent[] }>(`/api/app-config/history?key=${flag.key}`)).events);
    } catch (e) {
      setMessage({ kind: 'danger', text: e instanceof Error ? e.message : 'Could not load history' });
    }
  };

  return (
    <Stack hasGutter>
      {!flag.valueOnly && (
        <StackItem>
          <Switch
            id={`${flag.key}-enabled`}
            label={flag.hasClusterRules ? 'Restrictions on' : 'Enabled'}
            isChecked={enabled}
            onChange={(_e, checked) => setEnabled(checked)}
          />
        </StackItem>
      )}

      {flag.stringParamLabel && (
        <StackItem>
          <FormGroup label={flag.stringParamLabel} fieldId={`${flag.key}-string`}>
            <TextInput id={`${flag.key}-string`} value={stringValue} onChange={(_e, v) => setStringValue(v)} />
          </FormGroup>
        </StackItem>
      )}

      {flag.supportsAudience && (
        <StackItem>
          <FormGroup label="Affected users" role="radiogroup" fieldId={`${flag.key}-audience`}>
            <Radio id={`${flag.key}-all`} name={`${flag.key}-audience`} label="All users" isChecked={audienceMode === 'all'} onChange={() => setAudienceMode('all')} />
            <Radio
              id={`${flag.key}-groups`}
              name={`${flag.key}-audience`}
              label="Selected AD groups"
              isChecked={audienceMode === 'groups'}
              onChange={() => setAudienceMode('groups')}
            />
          </FormGroup>
          {audienceMode === 'groups' && (
            <div style={{ marginTop: 8 }}>
              <GroupPicker value={groups} onChange={setGroups} api={api} suggestions={suggestions} mine={mine} />
            </div>
          )}
        </StackItem>
      )}

      {flag.hasClusterRules && (
        <StackItem>
          <ClusterRulesEditor rules={rules} onChange={setRules} clusters={clusters} idPrefix={flag.key} api={api} suggestions={suggestions} mine={mine} />
        </StackItem>
      )}

      {message && (
        <StackItem>
          <Alert variant={message.kind} isInline title={message.text} />
        </StackItem>
      )}
      <StackItem>
        <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsMd' }}>
          <FlexItem>
            <Button variant="primary" onClick={save} isDisabled={busy} isLoading={busy}>
              Save
            </Button>
          </FlexItem>
          <FlexItem>
            <Button variant="secondary" onClick={reset} isDisabled={busy || !flag.saved}>
              Reset to default
            </Button>
          </FlexItem>
          <FlexItem>
            <Button variant="link" onClick={showHistory}>
              History
            </Button>
          </FlexItem>
        </Flex>
      </StackItem>

      <Modal variant={ModalVariant.large} title={`History: ${flag.label}`} isOpen={history !== null} onClose={() => setHistory(null)}>
        {history && history.length === 0 && <em>No recorded changes.</em>}
        {history && history.length > 0 && (
          <Stack hasGutter>
            <StackItem>
              <Switch id={`${flag.key}-raw`} label="Show raw JSON" isChecked={showRaw} onChange={(_e, checked) => setShowRaw(checked)} />
            </StackItem>
            <StackItem>
              {showRaw ? (
                history.map((e, i) => (
                  <div key={`${e.createdAt}-${i}`} style={{ marginBottom: 12 }}>
                    <strong>{new Date(e.createdAt).toLocaleString()}</strong> {e.userEmail}
                    <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{JSON.stringify({ before: e.eventPayload.before, after: e.eventPayload.after }, null, 2)}</pre>
                  </div>
                ))
              ) : (
                <Table aria-label={`History of ${flag.label}`} variant="compact">
                  <Thead>
                    <Tr>
                      <Th>When</Th>
                      <Th>Who</Th>
                      <Th>Setting</Th>
                      <Th>Before</Th>
                      <Th>After</Th>
                    </Tr>
                  </Thead>
                  {history.map((e, i) => {
                    const changes = describeChanges(e.eventPayload.before as never, e.eventPayload.after as never, flag);
                    const rows = changes.length > 0 ? changes : [{ setting: 'No change', before: '', after: '' }];
                    return (
                      <Tbody key={`${e.createdAt}-${i}`}>
                        {rows.map((c, j) => (
                          <Tr key={c.setting}>
                            <Td dataLabel="When">{j === 0 ? new Date(e.createdAt).toLocaleString() : ''}</Td>
                            <Td dataLabel="Who">{j === 0 ? e.userEmail : ''}</Td>
                            <Td dataLabel="Setting">{c.setting}</Td>
                            <Td dataLabel="Before" style={{ whiteSpace: 'pre-line' }}>{c.before}</Td>
                            <Td dataLabel="After" style={{ whiteSpace: 'pre-line' }}>{c.after}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    );
                  })}
                </Table>
              )}
            </StackItem>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
};
